import MiniSearch from 'minisearch';
import type { Item } from '../db/types';
import { previewText } from './markdown';

export interface SearchHit {
  item: Item;
  score: number;
  /** Lowercased terms that actually matched — what the UI highlights. */
  terms: string[];
}

const options = {
  fields: ['title', 'body', 'tagText'],
  storeFields: ['id'],
  searchOptions: {
    prefix: true,
    // Fuzzy by a quarter of the term's length: forgiving on long words,
    // strict on short ones, so "conf" does not match half the database.
    fuzzy: 0.25,
    boost: { title: 3, tagText: 2 },
  },
};

/**
 * Tags are ids on the item, so their *names* have to be resolved before they
 * can be indexed — otherwise searching for "urgent" would match nothing, which
 * is exactly what a user would expect to work.
 */
export interface IndexNames {
  lists: Map<string, string>;
  tags: Map<string, string>;
}

function indexable(item: Item, names?: IndexNames) {
  const tagText = [...item.list_tag_ids, ...item.global_tag_ids]
    .map((id) => names?.tags.get(id) ?? '')
    .filter(Boolean)
    .join(' ');
  const listText = names?.lists.get(item.list_id) ?? '';
  return {
    id: item.id,
    title: item.title,
    body: item.body,
    tagText: `${tagText} ${listText}`.trim(),
  };
}

/**
 * The whole index lives in memory and is rebuilt from Dexie whenever the item
 * set changes. For one user's lifetime of notes that is thousands of rows, not
 * millions — rebuilding costs less than keeping incremental updates honest.
 */
export function buildIndex(items: Item[], names?: IndexNames) {
  const index = new MiniSearch(options);
  index.addAll(items.map((item) => indexable(item, names)));
  return index;
}

export function search(index: MiniSearch, items: Item[], query: string): SearchHit[] {
  const q = query.trim();
  if (!q) return [];
  const byId = new Map(items.map((i) => [i.id, i]));
  return index
    .search(q)
    .map((r) => ({
      item: byId.get(r.id as string)!,
      score: r.score,
      terms: r.terms.map((t) => t.toLowerCase()),
    }))
    .filter((r) => r.item !== undefined);
}

export interface Segment {
  text: string;
  match: boolean;
}

/**
 * Splits text into alternating plain and matched runs, so the palette can
 * highlight exactly the span that matched rather than the whole row.
 */
export function highlight(text: string, terms: string[]): Segment[] {
  if (!terms.length || !text) return [{ text, match: false }];
  const lower = text.toLowerCase();
  const hits: Array<[number, number]> = [];

  for (const term of terms) {
    if (!term) continue;
    let from = 0;
    for (;;) {
      const at = lower.indexOf(term, from);
      if (at === -1) break;
      hits.push([at, at + term.length]);
      from = at + term.length;
    }
  }
  if (!hits.length) return [{ text, match: false }];

  // Merge overlaps so two terms hitting the same run produce one span.
  hits.sort((a, b) => a[0] - b[0]);
  const merged: Array<[number, number]> = [hits[0]!];
  for (const [start, end] of hits.slice(1)) {
    const last = merged[merged.length - 1]!;
    if (start <= last[1]) last[1] = Math.max(last[1], end);
    else merged.push([start, end]);
  }

  const out: Segment[] = [];
  let cursor = 0;
  for (const [start, end] of merged) {
    if (start > cursor) out.push({ text: text.slice(cursor, start), match: false });
    out.push({ text: text.slice(start, end), match: true });
    cursor = end;
  }
  if (cursor < text.length) out.push({ text: text.slice(cursor), match: false });
  return out;
}

/**
 * A one-line excerpt from the body centred on the first match, elided at both
 * ends — the "…silent conflict" preview in the command palette.
 */
export function excerpt(body: string, terms: string[], span = 48): string {
  // Strip markdown first, so a match never lands mid-syntax and the window
  // never spends its characters on `**` and `##`.
  const flat = previewText(body, Number.MAX_SAFE_INTEGER);
  if (!flat) return '';
  const lower = flat.toLowerCase();
  const at = terms.map((t) => lower.indexOf(t)).filter((i) => i >= 0).sort((a, b) => a - b)[0];
  if (at === undefined) return flat.slice(0, span);

  // Snap both edges out to whitespace. An excerpt that opens mid-word reads as
  // a rendering bug rather than as an elision.
  let start = Math.max(0, Math.round(at - span / 3));
  if (start > 0) {
    const space = flat.lastIndexOf(' ', start);
    start = space === -1 ? 0 : space + 1;
  }
  let end = Math.min(flat.length, start + span);
  if (end < flat.length) {
    const space = flat.indexOf(' ', end);
    end = space === -1 ? flat.length : space;
  }

  return `${start > 0 ? '…' : ''}${flat.slice(start, end)}${end < flat.length ? '…' : ''}`;
}
